'use client';

/**
 * FormSelectField (storefront, live) — the real <select>, registered with
 * react-hook-form. Falls back to the shared preview outside a FormContainer.
 */

import React from 'react';
import { FormSelectField as Shared, inputSurface } from '@launchstore/shared-puck';
import type { FormSelectFieldProps } from '@launchstore/shared-puck';
import { resolveColor } from '@launchstore/shared-puck';
import { useFormContext } from './FormContext';
import { buildValidationRules, fieldIds } from './form-live-helpers';

function FormSelectFieldLive(props: FormSelectFieldProps & { id?: string }) {
  const ctx = useFormContext();
  if (!ctx) return <>{(Shared.render as any)(props as FormSelectFieldProps)}</>;

  const { register, errors } = ctx;
  const ids = fieldIds(props.id);
  const error = errors[props.label];
  const rules = buildValidationRules(props);
  const surface = inputSurface(props);

  return (
    <div className={`mt-${props.marginTop || '0'} ${props.fieldWidth === 'half' ? 'w-1/2' : 'w-full'}`}>
      <label
        htmlFor={ids.input}
        className={`block text-${props.labelFontSize} font-${props.labelFontWeight} mb-1`}
        style={{ color: resolveColor(props.labelColor) }}
      >
        {props.label}
        {props.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      <select
        id={ids.input}
        className={`${surface.classes} ${error ? 'border-red-400' : ''}`}
        style={surface.style}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? ids.error : props.helpText ? ids.help : undefined}
        {...register(props.label, rules)}
      >
        <option value="">{props.placeholder || 'Select an option'}</option>
        {(props.options || []).map((opt, i) => (
          <option key={i} value={opt.label}>{opt.label}</option>
        ))}
      </select>

      {props.helpText && !error && (
        <p id={ids.help} className="mt-1 text-xs text-gray-500">{props.helpText}</p>
      )}
      {error && (
        <p role="alert" id={ids.error} className="mt-1 text-xs font-medium text-red-500">
          {String(error.message)}
        </p>
      )}
    </div>
  );
}

export const FormSelectField: any = {
  ...Shared,
  render: (props: any) => <FormSelectFieldLive {...props} />,
};

export default FormSelectField;
